import type { MouseEvent } from "react"
import { useEffect, useMemo, useState } from "react"
import { Link, useNavigate } from "@tanstack/react-router"
import { format } from "date-fns"
import DOMPurify from "dompurify"
import {
	IconCheckDoubleOutline18 as DoneIcon,
	IconInboxOutline18 as OpenIcon,
	IconClockOutline18 as SnoozedIcon,
} from "nucleo-ui-outline-18"
import { useToggle } from "react-use"

import type { BrandfetchData } from "@productlane/api/src/browser"
import type { Issue, Project } from "@productlane/zero"
import type {
	FeedbackWithRelations,
	TeamMember,
} from "@productlane/zero/queries"
import { feedbackOriginText } from "@productlane/lib"
import {
	cn,
	convertHtmlToText,
	removeAllBrTags,
} from "@productlane/lib/src/browser"
import {
	formatCompactTimestamp,
	formatRelativeTime,
} from "@productlane/lib/src/date"
import {
	Avatar,
	Badge,
	Checkbox,
	ContextMenu,
	ContextMenuContent,
	ContextMenuItem,
	ContextMenuTrigger,
	IssueStatusIcon,
	Tooltip,
} from "@productlane/ui"
import { mutators } from "@productlane/zero/mutators"

import type { DetailDrawerType } from "@/lib/detail-drawer"
import { MemberHoverCard } from "@/components/member-hover-card"
import { useDetailDrawer } from "@/lib/detail-drawer"
import { FeedbackOriginIconRecord } from "@/lib/feedback"
import { getProjectIcon } from "@/lib/projects"
import { useReadTransition } from "@/lib/use-read-transition"
import { mutateOk, useZero } from "@/lib/zero"
import { CompanyLogo } from "@/screens/app/customers/company-logo"
import { UserCompanyAvatar } from "@/screens/app/customers/user-company-avatar"
import {
	useLastThreadMessage,
	useLastThreadMessageAuthorLabel,
} from "@/screens/app/suggestion/use-thread-message"
import { LazyAssigneeSelect } from "@/screens/app/threads/components/right-bar/lazy-assignee-select"
import { isEmptyContentTag } from "@/screens/app/threads/helpers"
import { useThreadsMultiSelectState } from "@/screens/app/threads/multi-select/threads-multi-select-state"
import { RoleCheck } from "../shared/role-check"
import { toast } from "../toast"
import { ResponseBadges } from "./response-badges"
import { UnreadDot } from "./unread-dot"
import { useResponseBadge } from "./use-response-badge"

interface ThreadListItemSinglelineProps {
	thread: FeedbackWithRelations
	withResponseBadge?: boolean
	teamMembers: readonly TeamMember[]
	isActive?: boolean
}

type DetailContext = "company" | "contact" | "project" | "issue" | "tag"

function dedupeById<T extends { id: string }>(
	items: readonly T[] | undefined,
): T[] {
	if (!items) return []
	const seen = new Set<string>()
	const result: T[] = []
	for (const item of items) {
		if (seen.has(item.id)) continue
		seen.add(item.id)
		result.push(item)
	}
	return result
}

function useLastMessageHtml(thread: FeedbackWithRelations) {
	const lastMessageText = useLastThreadMessage(thread, "desc")

	return useMemo(
		() =>
			DOMPurify.sanitize(
				removeAllBrTags(
					convertHtmlToText(lastMessageText) ||
						convertHtmlToText(
							!isEmptyContentTag(thread?.text) ? (thread?.text ?? "") : "",
						),
				),
			),
		[thread.text, lastMessageText],
	)
}

export const ThreadListItemSinglelineSimple = ({
	thread,
}: ThreadListItemSinglelineProps) => {
	const zero = useZero()
	const read = useReadTransition(
		!thread?.isRead,
		"-translate-x-3.5",
		thread?.id,
	)

	if (!thread) return null

	return (
		<Link
			to="/threads/$id"
			params={{ id: thread.id }}
			className="box-content block select-none text-sm"
			onClick={(e) => {
				if (e.altKey) {
					e.preventDefault()
					zero.mutate(
						mutators.threads.update({
							id: thread.id,
							isRead: false,
						}),
					)
				} else if (!thread.isRead) {
					zero.mutate(
						mutators.threads.update({
							id: thread.id,
							isRead: true,
						}),
					)
				}
			}}
		>
			<div className="items-between px-l flex pr-6 pt-2.5 sm:py-1">
				<div className="flex w-1/5 min-w-52 flex-1 sm:flex-none">
					<div className="ml-2 flex h-11 w-14 flex-none items-center justify-center sm:ml-3">
						<div className="flex">
							<UserCompanyAvatar
								thread={thread}
								size="sm"
								brandfetchData={
									thread?.company?.companyMeta
										?.brandfetchData as unknown as BrandfetchData
								}
								name={thread?.contact?.name || thread?.contact?.email}
								colorSeed={thread?.contact?.email}
								image={thread?.contact?.imageUrl}
								linearLogoUrl={thread?.company?.logoUrl || undefined}
								painLevel={thread.painLevel}
							/>
						</div>
					</div>

					<div className="mr-0 flex min-w-36 flex-1 flex-col items-start gap-0 truncate sm:mr-5 sm:h-11 sm:flex-none sm:justify-center">
						<div className="flex flex-col items-start gap-0 sm:flex-row sm:items-center sm:gap-1.5">
							{!!thread?.company && (
								<div className="flex h-[1.2rem] max-w-36 text-base sm:text-sm">
									<span className="truncate font-medium text-primary">
										{thread.company.name}
									</span>
								</div>
							)}
							{thread?.contact ? (
								<div className="hidden h-[1.2rem] max-w-36 truncate capitalize tracking-tight text-tertiary sm:block">
									{thread.contact.name
										? thread.contact.name.split(" ")[0]
										: thread.contact.email.split(/[@.]/, 1)[0]}
								</div>
							) : !thread?.company ? (
								<div className="font-medium tracking-tight text-primary">
									No customer
								</div>
							) : null}
						</div>
					</div>
				</div>

				<div className="hidden min-w-0 flex-1 flex-row items-center sm:flex">
					<UnreadDot show={read.showDot} className={read.dotClassName} />
					<div
						className={cn(
							"mr-5 min-w-0 max-w-[80%] flex-shrink truncate tracking-tight",
							{
								"font-medium text-primary": !thread.isRead,
								"text-tertiary": thread.isRead,
							},
							read.slideClassName,
						)}
					>
						{thread.title}
					</div>
				</div>

				<div className="ml-6 flex items-center text-right text-xs">
					<ResponseBadges.CreatedAt
						time={new Date(thread.lastMessageAt)}
						isUnread={!thread.isRead}
					/>
				</div>

				<div className="ml-4 flex min-w-5 flex-none items-center justify-end">
					<MemberHoverCard
						userId={thread?.assigneeId}
						fallbackLabel="No assignee"
					>
						<Avatar
							showAvatarFallback={!thread?.assignee}
							image={thread?.assignee?.imageUrl}
							name={
								thread?.assignee?.name ||
								thread?.assignee?.email ||
								"No assignee"
							}
							size="xs"
						/>
					</MemberHoverCard>
				</div>
			</div>
		</Link>
	)
}

export const ThreadListItemSingleline = ({
	thread,
	withResponseBadge,
	teamMembers,
	isActive = false,
}: ThreadListItemSinglelineProps) => {
	const navigate = useNavigate()
	const [open, onOpenChange] = useToggle(false)
	const zero = useZero()

	const ResponseBadge = useResponseBadge(thread)
	const { threadIds, toggleThreadId } = useThreadsMultiSelectState()
	const isChecked = useMemo(() => {
		return threadIds.includes(thread?.id ?? "")
	}, [thread?.id, threadIds])

	const convertedLastMessage = useLastMessageHtml(thread)
	const read = useReadTransition(
		!thread?.isRead,
		"-translate-x-3.5",
		thread?.id,
	)

	// usePreloadTimelineEvents(thread?.id)

	if (!thread) {
		return null
	}

	return (
		<ContextMenu>
			<ContextMenuTrigger asChild>
				<Link
					to="/threads/$id"
					params={{ id: thread.id }}
					className="group box-content block select-none text-sm"
					onClick={(e) => {
						if (e.altKey) {
							e.preventDefault()
							zero.mutate(
								mutators.threads.update({
									id: thread.id,
									isRead: false,
								}),
							)
						} else if (!thread.isRead) {
							zero.mutate(
								mutators.threads.update({
									id: thread.id,
									isRead: true,
								}),
							)
						}
					}}
				>
					<div className="items-between relative flex pl-2 pr-6 pt-2.5 sm:py-1">
						<div className="flex w-1/5 min-w-52 flex-1 sm:flex-none">
							{/* Avatar container */}
							<div className="flex flex-col items-start justify-start sm:flex-row sm:items-center">
								<div className="group/avatar relative ml-2 flex h-11 w-14 flex-none items-center justify-center sm:ml-3">
									<RoleCheck not={["VIEWER"]}>
										<div
											className={cn(
												"absolute inset-0 z-10 hidden items-center justify-center group-hover/avatar:flex",
												{
													flex: isChecked,
												},
											)}
										>
											<Checkbox
												checked={isChecked}
												size="sm"
												onClick={(e: React.MouseEvent) => {
													e.preventDefault()
													toggleThreadId(thread.id)
												}}
											/>
										</div>
									</RoleCheck>
									<div
										className={cn("flex", {
											"group-hover/avatar:invisible": !isChecked,
											invisible: isChecked,
										})}
									>
										<UserCompanyAvatar
											thread={thread}
											size="sm"
											brandfetchData={
												thread?.company?.companyMeta
													?.brandfetchData as unknown as BrandfetchData
											}
											name={thread?.contact?.name || thread?.contact?.email}
											colorSeed={thread?.contact?.email}
											image={thread?.contact?.imageUrl}
											linearLogoUrl={thread?.company?.logoUrl || undefined}
											painLevel={thread.painLevel}
										/>
									</div>
								</div>
							</div>

							{/* Name and Company */}
							<div className="ml-2 mr-0 flex min-w-36 flex-1 flex-col items-start gap-0 truncate sm:mr-5 sm:h-11 sm:flex-none sm:justify-center">
								<div className="flex flex-col items-start gap-0 sm:flex-row sm:items-center sm:gap-1.5">
									{!!thread?.company && (
										<div className="flex h-[1.2rem] max-w-36 text-base sm:text-sm">
											<span
												className={cn("truncate", {
													"font-medium text-primary": !!thread?.contact,
													"font-medium": !thread?.contact,
												})}
											>
												{thread?.company.name}
											</span>
										</div>
									)}
									{thread?.contact ? (
										<div
											className={cn(
												"h-[1.2rem] max-w-36 truncate capitalize tracking-tight sm:block",
												{
													"hidden text-tertiary": !!thread?.company,
													"font-medium text-primary": !thread?.company,
												},
											)}
										>
											{thread?.contact.name
												? thread?.contact.name.split(" ")[0]
												: thread?.contact.email.split(/[@.]/, 1)[0]}
										</div>
									) : !thread?.company ? (
										<div className="font-medium tracking-tight text-primary">
											No customer
										</div>
									) : null}
								</div>

								{/* Title and Description (Small) */}
								<div className="flex pb-4 pr-6 pt-1 sm:hidden sm:pt-2">
									{/* <div className="ml-2 w-14 flex-none" /> */}
									<div className="flex-1 truncate">
										{thread.title && (
											<div className="flex items-center">
												<UnreadDot
													show={read.showDot}
													className={read.dotClassName}
												/>
												<div
													className={cn(
														"mr-5 truncate tracking-tight",
														{
															"font-medium text-primary": !thread.isRead,
															"text-tertiary": thread.isRead,
														},
														read.slideClassName,
													)}
												>
													{thread.title}
												</div>
											</div>
										)}

										<div
											className={cn(
												"truncate pr-10 text-xs tracking-tight",
												!thread.isRead || isActive
													? "text-secondary"
													: "text-tertiary",
											)}
											dangerouslySetInnerHTML={{
												__html: convertedLastMessage,
											}}
										/>
									</div>
								</div>

								{/* Badges below description (Mobile only) */}
								{withResponseBadge && thread.state !== "COMPLETED" && (
									<div className="flex flex-wrap items-center gap-2 pb-2 sm:hidden">
										{ResponseBadge?.extraBadges}
										{thread.painLevel === "HIGH" && (
											<ResponseBadges.Important className="ml-0 mr-0" />
										)}
										{ResponseBadge?.showWaitingClock && (
											<ResponseBadges.WaitingClock
												time={new Date(thread.lastMessageAt)}
											/>
										)}
									</div>
								)}
								{thread.state === "SNOOZED" && (
									<div className="flex flex-wrap items-center gap-2 pb-2 sm:hidden">
										{thread.painLevel === "HIGH" && (
											<ResponseBadges.Important className="ml-0 mr-0" />
										)}
									</div>
								)}
								{!withResponseBadge &&
									thread.state !== "SNOOZED" &&
									thread.state !== "COMPLETED" && (
										<div className="flex flex-wrap items-center gap-2 pb-2 sm:hidden">
											{thread.painLevel === "HIGH" && (
												<ResponseBadges.Important
													className="ml-0 mr-0"
													onClick={async (e: MouseEvent<HTMLDivElement>) => {
														e.preventDefault()
														await mutateOk(
															zero.mutate(
																mutators.threads.update({
																	id: thread.id,
																	painLevel: "UNKNOWN",
																}),
															),
														)
														toast.success({
															title: `Importance updated`,
															descriptionAction: {
																label: `${thread.title}`,
																callback: () =>
																	void navigate({
																		to: "/threads/$id",
																		params: { id: thread.id },
																	}),
															},
															description: "was marked as not important.",
														})
													}}
												/>
											)}
										</div>
									)}
								{/* Close Loop Badge (Mobile only) */}
								{thread.state === "COMPLETED" && (
									<div className="flex flex-wrap items-center gap-2 pb-2 sm:hidden">
										{thread.painLevel === "HIGH" && (
											<ResponseBadges.Important className="ml-0 mr-0" />
										)}
										<ResponseBadges.CloseLoop
											issues={thread.issues}
											projects={thread.projects}
											className="ml-0 mr-0"
										/>
									</div>
								)}
							</div>
						</div>

						{/* Title and Description (Medium) */}
						<div className="hidden min-w-0 flex-1 flex-row items-center sm:flex">
							{/* Close Loop Badge (Desktop only) */}
							{thread.state === "COMPLETED" && (
								<div className="mr-2 flex items-center gap-2">
									{thread.painLevel === "HIGH" && (
										<ResponseBadges.Important className="ml-0 mr-0" />
									)}
									<ResponseBadges.CloseLoop
										issues={thread.issues}
										projects={thread.projects}
										className="ml-0 mr-0"
									/>
								</div>
							)}
							{/* Badges before title (Desktop only) */}
							{withResponseBadge && thread.state !== "COMPLETED" && (
								<div className="mr-2 flex items-center gap-2">
									{ResponseBadge?.extraBadges}
									{thread.painLevel === "HIGH" && (
										<ResponseBadges.Important
											className="ml-0 mr-0"
											onClick={async (e: MouseEvent<HTMLDivElement>) => {
												e.preventDefault()
												await mutateOk(
													zero.mutate(
														mutators.threads.update({
															id: thread.id,
															painLevel: "UNKNOWN",
														}),
													),
												)
												toast.success({
													title: `Importance updated`,
													descriptionAction: {
														label: `${thread.title}`,
														callback: () =>
															void navigate({
																to: "/threads/$id",
																params: { id: thread.id },
															}),
													},
													description: "was marked as not important.",
												})
											}}
										/>
									)}
									{ResponseBadge?.showWaitingClock && (
										<ResponseBadges.WaitingClock
											time={new Date(thread.lastMessageAt)}
										/>
									)}
								</div>
							)}
							{thread.state === "SNOOZED" && thread.painLevel === "HIGH" && (
								<div className="mr-2 flex items-center gap-2">
									<ResponseBadges.Important
										className="ml-0 mr-0"
										onClick={async (e: MouseEvent<HTMLDivElement>) => {
											e.preventDefault()
											await mutateOk(
												zero.mutate(
													mutators.threads.update({
														id: thread.id,
														painLevel: "UNKNOWN",
													}),
												),
											)
											toast.success({
												title: `Importance updated`,
												descriptionAction: {
													label: `${thread.title}`,
													callback: () =>
														void navigate({
															to: "/threads/$id",
															params: { id: thread.id },
														}),
												},
												description: "was marked as not important.",
											})
										}}
									/>
								</div>
							)}
							{!withResponseBadge &&
								thread.state !== "SNOOZED" &&
								thread.state !== "COMPLETED" &&
								thread.painLevel === "HIGH" && (
									<div className="mr-2 flex items-center gap-2">
										<ResponseBadges.Important
											className="ml-0 mr-0"
											onClick={async (e: MouseEvent<HTMLDivElement>) => {
												e.preventDefault()
												await mutateOk(
													zero.mutate(
														mutators.threads.update({
															id: thread.id,
															painLevel: "UNKNOWN",
														}),
													),
												)
												toast.success({
													title: `Importance updated`,
													descriptionAction: {
														label: `${thread.title}`,
														callback: () =>
															void navigate({
																to: "/threads/$id",
																params: { id: thread.id },
															}),
													},
													description: "was marked as not important.",
												})
											}}
										/>
									</div>
								)}
							<UnreadDot show={read.showDot} className={read.dotClassName} />
							<div
								className={cn(
									"mr-5 min-w-0 max-w-[80%] flex-shrink truncate tracking-tight",
									{
										"font-medium text-primary": !thread.isRead,
										"text-tertiary": thread.isRead,
									},
									read.slideClassName,
								)}
							>
								{thread.title}
							</div>
							<div
								className={cn(
									"max-h-6 min-w-[20%] flex-1 overflow-hidden truncate pr-10 text-xs tracking-tight",
									!thread.isRead || isActive
										? "text-secondary"
										: "text-tertiary",
									read.slideClassName,
								)}
								dangerouslySetInnerHTML={{
									__html: convertedLastMessage,
								}}
							/>
						</div>

						{/* Time badge (Desktop only) */}
						<div className="ml-6 hidden items-center gap-2 text-right text-xs sm:flex">
							{withResponseBadge &&
							thread.state !== "COMPLETED" &&
							ResponseBadge ? (
								<ResponseBadges.Time
									time={new Date(thread.lastMessageAt)}
									isUnread={!thread.isRead || isActive}
								/>
							) : thread.state === "SNOOZED" ? (
								<ResponseBadges.UnsnoozedAt
									snoozedUntilIso={
										thread.snoozedUntil
											? new Date(thread.snoozedUntil).toISOString()
											: ""
									}
								/>
							) : (
								<ResponseBadges.CreatedAt
									time={new Date(thread.lastMessageAt)}
									isUnread={!thread.isRead || isActive}
								/>
							)}
						</div>

						{/* Assignee */}
						<LazyAssigneeSelect
							open={open}
							onOpenChange={onOpenChange}
							onChange={async (assigneeId) => {
								await mutateOk(
									zero.mutate(
										mutators.threads.update({
											id: thread.id,
											assigneeId,
										}),
									),
								)
							}}
							enableShortcut={false}
							teamMembers={teamMembers}
							thread={thread}
							assigneeId={thread?.assignee?.id}
						>
							<div className="ml-4 flex min-w-5 flex-none flex-col items-center justify-start gap-0.5 sm:flex-row">
								{/* Date above avatar (Mobile only) */}
								<div className="mb-1.5 flex items-center justify-center sm:hidden">
									{withResponseBadge &&
									thread.state !== "COMPLETED" &&
									ResponseBadge ? (
										<Tooltip
											label={format(
												new Date(thread.lastMessageAt),
												"EEE, MMM d 'at' h:mm a",
											)}
										>
											<span
												className={cn(
													"text-xs",
													!thread.isRead || isActive
														? "text-secondary"
														: "text-tertiary",
												)}
											>
												{formatRelativeTime(new Date(thread.lastMessageAt))}
											</span>
										</Tooltip>
									) : thread.state === "SNOOZED" ? (
										<ResponseBadges.UnsnoozedAt
											snoozedUntilIso={
												thread.snoozedUntil
													? new Date(thread.snoozedUntil).toISOString()
													: ""
											}
										/>
									) : (
										<ResponseBadges.CreatedAt
											time={new Date(thread.lastMessageAt)}
											isUnread={!thread.isRead || isActive}
										/>
									)}
								</div>
								<div
									className="flex items-center justify-end hover:brightness-75 dark:hover:brightness-125"
									onClickCapture={(e) => {
										e.preventDefault()

										onOpenChange(true)
									}}
								>
									<MemberHoverCard
										userId={thread?.assigneeId}
										fallbackLabel="No assignee"
									>
										<Avatar
											showAvatarFallback={!thread?.assignee}
											{...(!thread?.assignee
												? {
														fallbackProps: {
															className:
																"text-gray-900 dark:text-gray-400 bg-gray-150 dark:bg-gray-800",
														},
													}
												: {})}
											image={thread?.assignee?.imageUrl}
											name={
												thread?.assignee?.name
													? thread?.assignee?.name
													: (thread?.assignee?.email ?? "No assignee")
											}
											size="xs"
										/>
									</MemberHoverCard>
								</div>
							</div>
						</LazyAssigneeSelect>
					</div>
				</Link>
			</ContextMenuTrigger>
			<RoleCheck not={["VIEWER"]}>
				<ContextMenuContent>
					<ContextMenuItem
						onClick={async () => {
							if (thread.state === "PROCESSED") {
								await mutateOk(
									zero.mutate(
										mutators.threads.update({
											id: thread.id,
											state: "NEW",
											lastStateChangeAtIso: new Date().toISOString(),
										}),
									),
								)
								toast.success({
									title: "Moved to Inbox",
								})
							} else {
								await mutateOk(
									zero.mutate(
										mutators.threads.update({
											id: thread.id,
											state: "PROCESSED",
											lastStateChangeAtIso: new Date().toISOString(),
										}),
									),
								)
								toast.success({
									title: "Marked done",
								})
							}
						}}
					>
						{thread.state === "PROCESSED" ? "Move to Inbox" : "Mark done"}
					</ContextMenuItem>
				</ContextMenuContent>
			</RoleCheck>
		</ContextMenu>
	)
}

export const ThreadListItemSinglelineLazy: React.FC<
	ThreadListItemSinglelineProps
> = (props: ThreadListItemSinglelineProps) => {
	const [showFull, setShowFull] = useState(false)
	useEffect(() => {
		const timer = setTimeout(() => setShowFull(true), 10)
		return () => clearTimeout(timer)
	}, [])
	return showFull ? (
		<ThreadListItemSingleline {...props} />
	) : (
		<ThreadListItemSinglelineSimple {...props} />
	)
}

interface TagLike {
	id: string
	name: string
	color: string
	icon: string
}

const LINKED_BADGE_CLASS =
	"h-[1.55rem] max-w-[220px] rounded-full pl-1.5 pr-2 text-[0.6875rem] font-normal transition-colors hover:bg-gray-150 dark:hover:bg-gray-800"

function LinkedItemBadge({
	issue,
	project,
	tag,
	onNavigate,
}: {
	issue?: Issue
	project?: Project
	tag?: TagLike
	onNavigate: (to: string) => void
}) {
	const { open: openDetailDrawer } = useDetailDrawer()

	const handleClick = (to: string) => (e: MouseEvent<HTMLButtonElement>) => {
		e.preventDefault()
		e.stopPropagation()
		onNavigate(to)
	}

	const openDetail =
		(type: DetailDrawerType, id: string) =>
		(e: MouseEvent<HTMLButtonElement>) => {
			e.preventDefault()
			e.stopPropagation()
			openDetailDrawer(type, id)
		}

	if (issue) {
		const label = issue.identifier
			? `${issue.identifier}: ${issue.title}`
			: issue.title
		return (
			<Tooltip label={label}>
				<button type="button" onClick={openDetail("issue", issue.id)}>
					<Badge variant="default" className={LINKED_BADGE_CLASS}>
						<IssueStatusIcon
							status={issue.status}
							color={issue.statusColor || undefined}
							className="h-3.5 w-3.5 flex-none"
						/>
						<span className="truncate">{issue.title}</span>
					</Badge>
				</button>
			</Tooltip>
		)
	}

	if (project) {
		const ProjectIcon = getProjectIcon(project.icon)
		return (
			<Tooltip label={project.name}>
				<button type="button" onClick={openDetail("project", project.id)}>
					<Badge variant="default" className={LINKED_BADGE_CLASS}>
						<ProjectIcon
							className="h-3.5 w-3.5 flex-none"
							style={project.color ? { color: project.color } : undefined}
						/>
						<span className="truncate">{project.name}</span>
					</Badge>
				</button>
			</Tooltip>
		)
	}

	if (tag) {
		const TagIcon = getProjectIcon(tag.icon)
		return (
			<Tooltip label={tag.name}>
				<button type="button" onClick={handleClick(`/tags/${tag.id}`)}>
					<Badge variant="default" className={LINKED_BADGE_CLASS}>
						<TagIcon
							className="h-3.5 w-3.5 flex-none"
							style={{ color: tag.color }}
						/>
						<span className="truncate">{tag.name}</span>
					</Badge>
				</button>
			</Tooltip>
		)
	}

	return null
}

function ThreadStatusPill({
	state,
}: {
	state: FeedbackWithRelations["state"]
}) {
	const config =
		state === "PROCESSED" || state === "COMPLETED"
			? {
					label: "Done",
					Icon: DoneIcon,
					className:
						"bg-green-50/50 text-green-700 ring-green-500/40 dark:bg-green-500/10 dark:text-green-400 dark:ring-green-500/20",
				}
			: state === "SNOOZED"
				? {
						label: "Snoozed",
						Icon: SnoozedIcon,
						className:
							"bg-violet-50/50 text-violet-700 ring-violet-500/40 dark:bg-violet-500/10 dark:text-violet-400 dark:ring-violet-500/20",
					}
				: {
						label: "Open",
						Icon: OpenIcon,
						className:
							"bg-gray-100 text-secondary ring-gray-300/60 dark:bg-gray-800 dark:ring-gray-700",
					}

	return (
		<Tooltip label={config.label}>
			<Badge
				variant="default"
				className={cn(
					"flex-none rounded-full py-1 pl-1.5 pr-2 text-[0.6875rem] font-normal",
					config.className,
				)}
			>
				<config.Icon className="h-3 w-3 flex-none" />
				<span>{config.label}</span>
			</Badge>
		</Tooltip>
	)
}

export const ThreadListItemMultiline = ({
	thread,
	context,
}: ThreadListItemSinglelineProps & { context?: DetailContext }) => {
	const zero = useZero()
	const navigate = useNavigate()
	const lastMessageText = useLastThreadMessage(thread, "desc")
	const lastMessageAuthor = useLastThreadMessageAuthorLabel(thread, "desc")

	const convertedLastMessage = useMemo(
		() =>
			DOMPurify.sanitize(
				removeAllBrTags(
					convertHtmlToText(lastMessageText) ||
						convertHtmlToText(
							!isEmptyContentTag(thread?.text) ? (thread?.text ?? "") : "",
						),
				),
			),
		[thread?.text, lastMessageText],
	)

	if (!thread) {
		return null
	}

	const personLabel = thread.contact
		? thread.contact.name
			? thread.contact.name.split(" ")[0]
			: thread.contact.email.split(/[@.]/, 1)[0]
		: null

	const showCompany = context !== "company" && !!thread.company

	const issues = dedupeById(thread.issues)
	const projects = dedupeById(thread.projects)
	const tags = dedupeById(thread.tags)
	const hasBadges = issues.length > 0 || projects.length > 0 || tags.length > 0

	const OriginIcon = FeedbackOriginIconRecord[thread.origin]

	const navigateToPath = (to: string) => void navigate({ href: to })

	const navigateTo = (to: string) => (e: MouseEvent) => {
		e.preventDefault()
		e.stopPropagation()
		navigateToPath(to)
	}

	return (
		<Link
			to="/threads/$id"
			params={{ id: thread.id }}
			className="flex select-none flex-col gap-1.5 rounded-xl border border-gray-200 bg-white px-3.5 py-3 text-sm shadow-sm transition-colors hover:border-gray-300 hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-900 dark:shadow-none dark:hover:border-gray-700 dark:hover:bg-gray-850"
			onClick={(e) => {
				if (e.altKey) {
					e.preventDefault()
					zero.mutate(
						mutators.threads.update({
							id: thread.id,
							isRead: false,
						}),
					)
				} else if (!thread.isRead) {
					zero.mutate(
						mutators.threads.update({
							id: thread.id,
							isRead: true,
						}),
					)
				}
			}}
		>
			<div className="flex items-center gap-2">
				{personLabel ? (
					<button
						type="button"
						onClick={navigateTo(`/people/${thread.contact?.id}`)}
						className="flex min-w-0 items-center gap-1.5 rounded-md transition-colors hover:text-secondary"
					>
						<Avatar
							image={thread.contact?.imageUrl}
							name={thread.contact?.name || thread.contact?.email || ""}
							colorSeed={thread.contact?.email}
							size="xs"
						/>
						<span className="truncate capitalize text-primary">
							{personLabel}
						</span>
					</button>
				) : thread.company ? (
					<button
						type="button"
						onClick={navigateTo(`/customers/${thread.company.id}`)}
						className="flex min-w-0 items-center gap-1.5 rounded-md transition-colors hover:text-secondary"
					>
						<CompanyLogo
							size="xs"
							fallback={thread.company.name}
							brandfetchData={
								thread.company.companyMeta?.brandfetchData as
									| BrandfetchData
									| undefined
							}
							linearLogoUrl={thread.company.logoUrl || undefined}
						/>
						<span className="truncate text-primary">{thread.company.name}</span>
					</button>
				) : (
					<span className="text-tertiary">No person selected</span>
				)}

				{showCompany && personLabel && thread.company ? (
					<button
						type="button"
						onClick={navigateTo(`/customers/${thread.company.id}`)}
						className="flex min-w-0 items-center gap-1.5 truncate text-tertiary transition-colors hover:text-secondary"
					>
						<span>⋅</span>
						<span className="truncate">{thread.company.name}</span>
					</button>
				) : null}

				<div className="flex-1" />

				<ThreadStatusPill state={thread.state} />

				<Tooltip
					label={feedbackOriginText(thread.origin, thread.customChannel)}
				>
					<Badge
						variant="default"
						className="flex-none rounded-full p-1 text-tertiary"
					>
						<OriginIcon className="h-3 w-3 flex-none" />
					</Badge>
				</Tooltip>

				<MemberHoverCard
					userId={thread.assigneeId}
					fallbackLabel="No assignee"
					className="flex-none"
				>
					<Avatar
						showAvatarFallback={!thread.assignee}
						image={thread.assignee?.imageUrl}
						name={
							thread.assignee?.name || thread.assignee?.email || "No assignee"
						}
						size="xs"
					/>
				</MemberHoverCard>
			</div>

			<div
				className={cn("line-clamp-1 text-sm font-medium", {
					"text-primary": !thread.isRead,
					"text-secondary": thread.isRead,
				})}
			>
				{thread.number != null && (
					<span className="mr-1.5 text-tertiary">#{thread.number}</span>
				)}
				{thread.title || convertedLastMessage}
			</div>

			<div className="line-clamp-2 w-full text-sm text-tertiary">
				{lastMessageAuthor && (
					<span className="font-medium text-secondary">
						{lastMessageAuthor}:{" "}
					</span>
				)}
				<span dangerouslySetInnerHTML={{ __html: convertedLastMessage }} />
			</div>

			<div className="mt-0.5 flex items-end gap-1.5">
				{hasBadges && (
					<div className="flex min-w-0 flex-wrap items-center gap-1.5">
						{issues.map((issue) => (
							<LinkedItemBadge
								key={issue.id}
								issue={issue}
								onNavigate={navigateToPath}
							/>
						))}
						{projects.map((project) => (
							<LinkedItemBadge
								key={project.id}
								project={project}
								onNavigate={navigateToPath}
							/>
						))}
						{tags.map((tag) => (
							<LinkedItemBadge
								key={tag.id}
								tag={tag}
								onNavigate={navigateToPath}
							/>
						))}
					</div>
				)}
				<Tooltip
					label={
						<div className="flex flex-col gap-0.5">
							<span>
								Created:{" "}
								<span className="text-tertiary">
									{format(
										new Date(thread.createdAt),
										"MMM d, yyyy 'at' h:mm a",
									)}
								</span>
							</span>
							<span>
								Last message:{" "}
								<span className="text-tertiary">
									{format(
										new Date(thread.lastMessageAt),
										"MMM d, yyyy 'at' h:mm a",
									)}
								</span>
							</span>
						</div>
					}
				>
					<span className="ml-auto flex h-[1.55rem] flex-none items-center text-xs text-tertiary">
						{formatCompactTimestamp(new Date(thread.lastMessageAt))}
					</span>
				</Tooltip>
			</div>
		</Link>
	)
}
